import {
  asapScheduler,
  BehaviorSubject,
  map,
  Observable,
  observeOn,
  share,
  shareReplay,
  Subject,
} from 'rxjs';
import {
  MapFilterItem,
  MapFilterState,
  MapFilterType,
} from './map-filter-state.model';
import {
  ArrayUtils,
  EditsOperationsLike,
  EditsResultLike,
} from '../../../../utils/array.utils';
import { ObjectUtils } from '../../../../utils/object.utils';
import { InjectionToken } from '@angular/core';

type FilterExprFn = (x: MapFilterItem) => boolean;

export const ACTIVE_FILTERS_ON_MAP_MANAGER =
  new InjectionToken<MapFilterManager>('ACTIVE_FILTERS_ON_MAP_MANAGER');

export class MapFilterManager {
  private _state: MapFilterState;

  private _loadStateSubject = new Subject<MapFilterState>();
  private _stateSubject: Subject<MapFilterState>;

  readonly onStateLoaded: Observable<MapFilterState>;
  readonly onStateChanges: Observable<MapFilterState>;
  readonly onFiltersChanged: Observable<MapFilterItem[]>;

  constructor() {
    this._state = new MapFilterState();

    this.onStateLoaded = this._loadStateSubject
      .asObservable()
      .pipe(observeOn(asapScheduler));

    this._stateSubject = new BehaviorSubject(this._state);

    const stateObservable = this._stateSubject
      .asObservable()
      .pipe(observeOn(asapScheduler));

    this.onStateChanges = stateObservable.pipe(map((state) => state.clone()));
    this.onFiltersChanged = stateObservable.pipe(
      map((state) => ObjectUtils.clone(state.filters))
    );
  }

  loadState(item: MapFilterState) {
    item = new MapFilterState(item);
    this._emitState(item, true);
  }

  getState() {
    return this._state.clone();
  }

  getFilters() {
    return ObjectUtils.clone(this._state.filters);
  }

  addFilter(filter: Partial<MapFilterItem>) {
    this.addFilters([filter]);
  }

  addFilters(filters: Partial<MapFilterItem>[]) {
    this.applyEditsFilters({ adds: filters });
  }

  addOrUpdateFilters(filters: Partial<MapFilterItem>[]) {
    this.applyEditsFilters({ adds: filters, updates: filters });
  }

  removeFilter(item: Partial<MapFilterItem>) {
    this.removeFilterRange([item]);
  }

  removeFilterRange(items: Partial<MapFilterItem>[]) {
    this.removeFilterRangeByKeys(items?.map(commonKeyExtractor));
  }

  removeFilterByKey(key: string) {
    this.removeFilterRangeByKeys([key]);
  }

  removeFilterRangeByKeys(keys: string[]) {
    this.applyEditsFilters({ deletes: keys });
  }

  removeAllFilters() {
    this.removeFilterRange(this._state.filters);
  }

  applyEditsFilters(
    edits: EditsOperationsLike<Partial<MapFilterItem>, string>
  ) {
    let adds = edits?.adds
      ?.filter((x) => !!x)
      ?.map((x) => new MapFilterItem(x));
    let updates = edits?.updates
      ?.filter((x) => !!x)
      ?.map((x) => new MapFilterItem(x));
    let deletes = edits?.deletes?.filter((x) => !!x) ?? [];

    const editsAux: EditsOperationsLike<MapFilterItem, string> = {
      adds,
      updates,
      deletes,
    };

    if (!this._isValidChangesNotify(editsAux)) {
      return;
    }

    const state = this._state.clone();
    const changes = ArrayUtils.applyEdits(
      state.filters,
      editsAux,
      commonKeyExtractor,
      null,
      ObjectUtils.notEquals
    );

    if (this._isValidChangesNotify(changes)) {
      this._emitState(state);
    }
  }

  listFiltersByTypeAndField(type: MapFilterType, field: string) {
    return this.listFiltersBy({ type, field });
  }

  getFilterByTypeAndField(type: MapFilterType, field: string) {
    return this.getFilterBy({ type, field });
  }

  listFilterKeysBy(objOrFn: Partial<MapFilterItem> | FilterExprFn) {
    return this._listFiltersBy(objOrFn).map((x) => x.id);
  }

  listFiltersBy(objOrFn: Partial<MapFilterItem> | FilterExprFn) {
    return ObjectUtils.clone(this._listFiltersBy(objOrFn));
  }

  getFilterBy(objOrFn: Partial<MapFilterItem> | FilterExprFn) {
    return ObjectUtils.clone(this._getFilterBy(objOrFn));
  }

  getFilterKeyBy(objOrFn: Partial<MapFilterItem> | FilterExprFn) {
    return this._getFilterBy(objOrFn)?.id;
  }

  private _getFilterBy(objOrFn: Partial<MapFilterItem> | FilterExprFn) {
    return this._state.filters.find(this._generateFilterExpr(objOrFn));
  }

  private _listFiltersBy(objOrFn: Partial<MapFilterItem> | FilterExprFn) {
    return this._state.filters.filter(this._generateFilterExpr(objOrFn));
  }

  private _generateFilterExpr(objOrFn: Partial<MapFilterItem> | FilterExprFn) {
    if (typeof objOrFn === 'function') {
      return objOrFn;
    }

    const keys = Object.keys(objOrFn);

    let filterFn: (x: MapFilterItem) => boolean;

    for (const key of keys) {
      const filterFnAux = filterFn;
      filterFn = (x) => {
        if (filterFnAux && !filterFnAux(x)) {
          return false;
        }

        return ObjectUtils.equals(x[key], objOrFn[key]);
      };
    }

    if (!filterFn) {
      filterFn = () => true;
    }

    return filterFn;
  }

  private _isValidChangesNotify(
    changes: EditsResultLike | EditsOperationsLike
  ) {
    return !!(
      changes?.adds?.length ||
      changes?.updates?.length ||
      changes?.deletes?.length
    );
  }

  private _emitState(state: MapFilterState, shouldEmitLoad = false) {
    this._state = state;

    if (shouldEmitLoad) {
      this._loadStateSubject.next(state.clone());
    }

    this._stateSubject.next(this._state);
  }
}

function commonKeyExtractor(item: Partial<MapFilterItem>) {
  return item?.id;
}
